import { Box, Typography, Button } from "@mui/material";
import { toToman } from "../../utils/Utils";
import { useRequest } from "../../api/utils/useRequest";
import { BalootUserV2APIs } from "../../api/APIs";
import { useCallback, useEffect, useState } from "react";
import DottedTitleDivider from "../shared/DottedTitleDivider";
import BalootAreaChart from "../shared/BalootAreaChart";
import moment from "moment-jalaali";
import PortfolioItemCard from "./PortfolioItemCard";
import BalootPieChart from "../shared/BalootPieChart";
import CustomRectangularSkeleton from "../shared/CustomRectangularSkeleton";
import { ReactComponent as TodayEmptyState } from "../../assets/empty-states/today-empty-state.svg";
import { useNavigate } from "react-router-dom";

export default function AssetsToday() {
  const navigate = useNavigate();
  const [periodIndex, setPeriodIndex] = useState(0);
  const {
    loading,
    result,
    apiCall: getUserInvestmentToday,
  } = useRequest(BalootUserV2APIs.getUserInvestmentToday);

  useEffect(() => {
    getUserInvestmentToday();
  }, []);

  const getPeriodData = useCallback(
    (index, data) => {
      if (!index) return data;
      const now = moment().unix() * 1000;
      let fromDate =
        moment()
          .subtract(index + 1, "jMonth")
          .unix() * 1000;
      const result = data.filter((item) => {
        const itemDate = new Date(item.date).getTime();
        return itemDate <= now && itemDate >= fromDate;
      });
      return result;
    },
    [periodIndex, result]
  );

  const periodIndexName = (index) => {
    switch (index) {
      case 0:
        return "۱ ماهه";
      case 1:
        return "۳ ماهه";
      case 2:
        return "۶ ماهه";
      case 3:
        return "از ابتدا";
      default:
        break;
    }
  };

  return (
    <>
      {loading ? (
        <>
          <CustomRectangularSkeleton />
          <CustomRectangularSkeleton sx={{ mt: 2 }} height={100} />
          <CustomRectangularSkeleton sx={{ mt: 2 }} height={40} />
          <CustomRectangularSkeleton sx={{ mt: 2 }} height={40} />
          <CustomRectangularSkeleton sx={{ mt: 2 }} height={40} />
        </>
      ) : result && result.data.length === 0 && result.wallet.amount === 0 ? (
        <Box
          sx={{
            mt: 4,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <TodayEmptyState width={160} height={165} />
          <Typography mt={2} variant="subtitle1" color="grey.4">
            شروع به سرمایه‌گذاری کن تا اینجا عملکردت رو ببینی.
          </Typography>
          <Button
            onClick={() => navigate("../investment")}
            variant="contained"
            color="primary"
            size="small"
            sx={{ color: "common.white", mt: 1, typography: "h6" }}
          >
            شروع سرمایه‌گذاری
          </Button>
        </Box>
      ) : (
        <>
          {result && result.data.length !== 0 && (
            <>
              <Box
                sx={{
                  flex: 1,
                  py: "16px",
                  px: "12px",
                  boxShadow: (theme) => theme.shadows[2],
                  bgcolor: "common.white",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="subtitle1" color="grey.2">
                  {`${
                    result.investmentInfo[periodIndex].totalAmount > 0
                      ? "سود"
                      : "زیان"
                  } ${periodIndexName(periodIndex)}`}
                </Typography>

                <Typography
                  variant="subtitle1"
                  color={
                    result.investmentInfo[periodIndex].totalAmount > 0
                      ? "primary.main"
                      : "red.main"
                  }
                  fontSize={14}
                >{`${toToman(
                  result.investmentInfo[periodIndex].totalAmount
                )} تومان`}</Typography>
              </Box>
              <BalootAreaChart data={getPeriodData(periodIndex, result.data)} />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {[1, 3, 6, 0].map((item, index) => {
                  const isActive = index === periodIndex;
                  return (
                    <Box
                      key={item}
                      onClick={() => setPeriodIndex(index)}
                      sx={{
                        bgcolor: isActive ? "grey.2" : "common.white",
                        borderRadius: "16px",
                        mx: "2px",
                        minWidth: 60,
                        p: "4px",
                      }}
                    >
                      <Typography
                        align="center"
                        variant="body2"
                        color={isActive ? "common.white" : "grey.3"}
                      >
                        {!item ? `از ابتدا` : `${item}ماه`}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
          {result && (
            <>
              <DottedTitleDivider my={3} title="ترکیب دارایی‌ها" />
              <Box
                sx={{
                  border: (theme) => `1px solid ${theme.palette.grey[5]}`,
                  borderRadius: "12px",
                  py: 1,
                  px: 2,
                  mb: 3,
                }}
              >
                {[
                  {
                    title: "سرمایه‌گذاری در انتظار : ",
                    value: `${toToman(result.pendingInvestmentAmount)} تومان`,
                  },
                  {
                    title: "ارزش سرمایه‌گذاری فعلی : ",
                    value: `${toToman(result.presentInvestmentAmount)} تومان`,
                  },
                  {
                    title: "موجودی قلک : ",
                    value: `${toToman(result.wallet.amount)} تومان`,
                  },
                  {
                    title: "ارزش دارایی : ",
                    value: `${toToman(result.totalAssetAmount)} تومان`,
                  },
                ].map((item) => {
                  return (
                    <Box
                      key={item.title}
                      sx={{
                        my: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography ml={1} variant="subtitle1" color="grey.3">
                        {item.title}
                      </Typography>
                      <Typography variant="subtitle1" color={"grey.2"}>
                        {item.value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
          {result && result.data.length !== 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: (theme) => `1px solid ${theme.palette.grey[4]}`,
                }}
              >
                {["نام صندوق", "ارزش سرمایه‌گذاری", "سود / زیان"].map(
                  (item) => (
                    <Typography
                      key={item}
                      variant="subtitle1"
                      color="grey.3"
                      py={1}
                    >
                      {item}
                    </Typography>
                  )
                )}
              </Box>
              {result.portfolio
                .filter((item) => Boolean(item.assetId))
                .map((item) => {
                  return (
                    <div
                      key={item.assetId}
                      onClick={() =>
                        navigate(`/app/financial-asset/${item.assetId}`, {
                          state: { assetName: item.name },
                        })
                      }
                    >
                      <PortfolioItemCard portfolioItem={item} />
                    </div>
                  );
                })}
            </>
          )}
          {result &&
            result.portfolio.length >= 1 &&
            (result.wallet.amount > 0 || result.portfolio.length > 1) && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 3,
                }}
              >
                <BalootPieChart data={result.portfolio} />
                <Box
                  sx={{
                    mt: 2,
                    width: "100%",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {result.portfolio.map((item, index) => {
                    return (
                      <Box
                        key={item.name}
                        sx={{
                          p: 1,
                          boxShadow: (theme) => theme.shadows[1],
                          borderRadius: "14px",
                          mx: "4px",
                          my: "4px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            p: "4px",
                            bgcolor: (theme) => theme.palette.chart[index].main,
                            borderRadius: "8px",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: (theme) => theme.palette.chart[index].text,
                            }}
                          >
                            {item.percentage === 0
                              ? `کمتر از 0.01%`
                              : `%${item.percentage}`}
                          </Typography>
                        </Box>
                        <Typography ml={1} variant="subtitle1" color="grey.2">
                          {item.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
        </>
      )}
    </>
  );
}
